import { useState } from 'react';

import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Button, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { convertStringsToUndefined } from '@npi/buddy-utils';
import { Formik } from 'formik';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation } from '@redwoodjs/web';

import { Accordian, FieldInput } from 'src/components';
import { Flex } from 'src/components/Flex';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { DEFAULT_BORDER } from 'src/constants';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { CREATE_TRANSACTION_PRICING_OFFER } from 'src/graphql/mutations/createTransactionPricingOffer.mutation';
import { PageContent } from 'src/layouts/MainLayout/PageContent';

import { ADD_TRANSACTION_PRICING_INITIAL_VALUES } from './initialValues';
import ManageEligibilityModal from './ManageEligibilityModal';
import { AdditionalTariffs, CompliancePricing, TransactionPricing } from './sections';
import { TRANSACTION_PRICING_OFFER_VALIDATION_SCHEMA } from './validationSchemas';

const initialValues = ADD_TRANSACTION_PRICING_INITIAL_VALUES();

const AddTransactionPricingPage = () => {
    const transactionPricingRef = React.useRef(null);
    const additionalTarrifTableRef = React.useRef(null);
    const pciCompliancePricingRefRef = React.useRef(null);
    const [showEligibility, setShowEligibility] = useState(false);
    const { isAdmin } = useAuthContext();

    const [createTransactionPricingOffer] = useMutation(CREATE_TRANSACTION_PRICING_OFFER, {
        onError: () => {
            return showErrorToast('Transaction Pricing was not created successfully');
        },
        onCompleted: () => {
            showSuccessToast('Successfully created Transaction Pricing Offer');
            navigate('/transaction-pricing');
        },
    });

    const handleShowEligibilityModal = () => {
        setShowEligibility((prev) => {
            return !prev;
        });
    };

    const closeHandleShowEligibilityModal = () => {
        setShowEligibility(false);
    };

    return (
        <PageContent heading="Transaction Pricing Offers">
            <MetaTags title="Add Transaction Pricing" description="Add Transaction Pricing page" />

            <Formik
                initialValues={initialValues}
                onSubmit={async (input) => {
                    input.transactionPricing = convertStringsToUndefined(input.transactionPricing);

                    await createTransactionPricingOffer({
                        variables: {
                            input: {
                                ...input,
                                revenueShare: Number(input.revenueShare),
                            },
                        },
                    });
                }}
                enableReinitialize
                validationSchema={TRANSACTION_PRICING_OFFER_VALIDATION_SCHEMA}
            >
                {(formikProps) => {
                    return (
                        <>
                            <Stack spacing={5}>
                                <Box
                                    sx={{
                                        ...DEFAULT_BORDER,
                                        boxShadow: '0px 4px 20px 0px #2632381A',
                                    }}
                                >
                                    <Box
                                        padding="16px"
                                        bgcolor="#F39E4E"
                                        borderRadius="16px 16px 0px 0px"
                                    >
                                        <Typography
                                            fontSize="20px"
                                            fontWeight="500"
                                            lineHeight="24px"
                                            color="#fff"
                                        >
                                            Add Transaction Pricing Offers
                                        </Typography>
                                    </Box>
                                    <Flex flexDirection="column" padding="24px" gap="24px">
                                        <Button
                                            startIcon={<SettingsIcon />}
                                            onClick={handleShowEligibilityModal}
                                            sx={{
                                                bgcolor: 'transparent',
                                                color: '#F18F34',
                                                border: '#F18F34 solid 1px',
                                                alignSelf: 'flex-end',
                                            }}
                                        >
                                            Manage Eligibility
                                        </Button>
                                        <Flex gap="24px">
                                            <FieldInput
                                                name="offerName"
                                                label="Pricing Offer Name"
                                            />
                                            <FieldInput
                                                name="revenueShare"
                                                label="Revenue Share (%)"
                                                placeholder="Revenue Share"
                                            />
                                        </Flex>
                                        <Flex justifyContent="flex-end" gap="24px">
                                            <Button
                                                variant="main"
                                                sx={{ bgcolor: '#686867', width: '20%' }}
                                                onClick={() => {
                                                    return navigate('/transaction-pricing');
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="main"
                                                sx={{ width: '20%' }}
                                                onClick={async () => {
                                                    try {
                                                        await TRANSACTION_PRICING_OFFER_VALIDATION_SCHEMA.validate(
                                                            formikProps.values
                                                        );

                                                        formikProps.handleSubmit();
                                                    } catch (error) {
                                                        if (formikProps.errors.transactionPricing) {
                                                            transactionPricingRef.current.scrollIntoView(
                                                                {
                                                                    behavior: 'smooth',
                                                                    block: 'start',
                                                                }
                                                            );
                                                        } else if (
                                                            formikProps.errors.additionalTariffs
                                                        ) {
                                                            additionalTarrifTableRef.current.scrollIntoView(
                                                                {
                                                                    behavior: 'smooth',
                                                                    block: 'start',
                                                                }
                                                            );
                                                        } else if (
                                                            formikProps.errors.compliancePricing
                                                        ) {
                                                            pciCompliancePricingRefRef.current.scrollIntoView(
                                                                {
                                                                    behavior: 'smooth',
                                                                    block: 'start',
                                                                }
                                                            );
                                                        }

                                                        showErrorToast(
                                                            'Please fill in required fields'
                                                        );
                                                    }
                                                }}
                                            >
                                                Add Transaction Offer
                                            </Button>
                                        </Flex>

                                        <Accordian
                                            heading="Transaction Pricing"
                                            ref={transactionPricingRef}
                                        >
                                            <TransactionPricing
                                                name="transactionPricing"
                                                notAdminUser={!isAdmin}
                                            />
                                        </Accordian>

                                        <Accordian
                                            heading="Additional Tariffs Table"
                                            ref={additionalTarrifTableRef}
                                        >
                                            <AdditionalTariffs
                                                name="additionalTariffs"
                                                notAdminUser={!isAdmin}
                                            />
                                        </Accordian>

                                        <Accordian
                                            heading="PCI Compliance Pricing"
                                            ref={pciCompliancePricingRefRef}
                                        >
                                            <CompliancePricing
                                                name="compliancePricing"
                                                notAdminUser={!isAdmin}
                                            />
                                        </Accordian>

                                        {/* Add transaction pricing Modal, Manage Eligibility */}
                                    </Flex>
                                </Box>
                            </Stack>

                            <Dialog
                                open={showEligibility}
                                onClose={closeHandleShowEligibilityModal}
                                aria-labelledby="delete-user-dialog-title"
                                sx={{
                                    '& .MuiDialog-paper': {
                                        width: '100%',
                                        padding: '0',
                                    },
                                }}
                                maxWidth="lg"
                            >
                                <DialogContent style={{ padding: '0' }}>
                                    <ManageEligibilityModal
                                        title="Manage Eligibility"
                                        closeHandleShowEligibilityModal={
                                            closeHandleShowEligibilityModal
                                        }
                                    />
                                </DialogContent>
                            </Dialog>
                        </>
                    );
                }}
            </Formik>
        </PageContent>
    );
};

export default AddTransactionPricingPage;
